import {createStyles, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import {SelectedParentStorable} from 'constants/SampleConstants';
import notifications from 'notifications';
import {
  BulkPutScratchPlateInInventoryItem,
  listScratchPlateMappableColumns,
  MappableLayer,
  ScratchPlateColumnMapping,
} from 'plates/dao/ScratchPlateDAO';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import BarcodeInput from 'samples/BarcodeInput';
import BaseStorablePaneStudyableFormGroup from 'samples/BaseStorablePaneStudyableFormGroup';
import ParentStorableDataSelector from 'samples/ParentStorableDataSelector';
import {submitBulkPutInInventory} from 'samples/plate-maps/scratch-plates/ScratchPlateInventoryActions';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import {PlateSchema, PlateSchemaType, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {ConnectedStorableSourceSelector} from 'samples/storables/StorableSourceSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

import ScratchPlateColumnMappingTable from './ScratchPlateColumnMappingTable';

export interface ScratchPlateInput {
  scratchPlateId: string;
  scratchPlateName: string | null;
  plateSize: PlateSize;
  plateSchemaId: string | null;
}

export interface PutScratchPlateInInventoryModalProps {
  plates: Array<ScratchPlateInput>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onCompleted?: () => void;
}

function PutScratchPlateInInventoryModal({
  plates,
  onRequestClose,
  onRequestSubmit,
  onCompleted,
}: PutScratchPlateInInventoryModalProps): JSX.Element {
  // The modal seeds its shared fields from plates[0]. openPutScratchPlateInInventoryModal
  // guards this too, but the component is exported and rendered directly (e.g. in tests),
  // so fail fast with a clear message rather than crashing on plates[0] being undefined.
  if (plates.length === 0) {
    throw new Error('PutScratchPlateInInventoryModal requires at least one plate.');
  }
  const {classes} = useStyles();
  const isSinglePlate = plates.length === 1;
  // All selected plates come from one design and so share dimensions; use the
  // first plate to filter schemas and to seed the shared schema default.
  const plateSize = plates[0].plateSize;
  const initialPlateSchemaId = plates[0].plateSchemaId;

  const [isSubmitting, setIsSubmitting] = useState(false);
  // A successful submit calls onRequestSubmit, which unmounts this modal. The submit's
  // `finally` would then setState on an unmounted component, so guard against that.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Per-plate name overrides, keyed by scratch plate id, seeded from each
  // plate's current name. This is the only field that varies per plate.
  const [namesById, setNamesById] = useState<Record<string, string>>(() =>
    Object.fromEntries(plates.map((plate) => [plate.scratchPlateId, plate.scratchPlateName ?? '']))
  );

  const [shouldAutogenerateBarcode, setShouldAutogenerateBarcode] = useState(true);
  const [customBarcode, setCustomBarcode] = useState('');
  const [selectedPlateSchemaId, setSelectedPlateSchemaId] = useState<string | null>(initialPlateSchemaId);

  const [selectedParent, setSelectedParent] = useState<SelectedParentStorable>(getEmptySelectedParent);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [studyIds, setStudyIds] = useState<Array<string>>([]);

  const [mappableLayers, setMappableLayers] = useState<Array<MappableLayer>>([]);
  const [columnMappings, setColumnMappings] = useState<Array<ScratchPlateColumnMapping>>([]);

  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    SampleSchemaStore.addChangeListener(forceUpdate);
    // Container schemas hold the settable fields that columns map onto; ensure
    // they are loaded so selectedContainerSchema resolves once a schema is picked.
    SampleSchemaActions.fetchAllContainerSchemas();
    return () => {
      SampleSchemaStore.removeChangeListener(forceUpdate);
    };
  }, []);

  // The mappable source columns live on the scratch plate, independent of the
  // chosen plate schema. The shared mapping applies to every plate, so fetch
  // the representative set from the first plate once.
  const representativePlateId = plates[0].scratchPlateId;
  useEffect(() => {
    let cancelled = false;
    listScratchPlateMappableColumns(representativePlateId)
      .then((response) => {
        if (!cancelled) {
          setMappableLayers(response.layers);
        }
      })
      .catch(() => {
        // Column mapping is optional; failing to load source columns simply
        // hides the mapping table rather than blocking plate creation.
      });
    return () => {
      cancelled = true;
    };
  }, [representativePlateId]);

  const selectedPlateSchema: PlateSchema | null =
    selectedPlateSchemaId != null ? SampleSchemaStore.getPlateSchema(selectedPlateSchemaId) : null;

  const selectedContainerSchema =
    selectedPlateSchema?.containerSchemaId != null
      ? SampleSchemaStore.getContainerSchema(selectedPlateSchema.containerSchemaId)
      : null;

  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean => schema.height === plateSize[0] && schema.width === plateSize[1],
    [plateSize]
  );

  const handlePlateSchemaChange = useCallback((schema: PlateSchema | null) => {
    setSelectedPlateSchemaId(schema?.id ?? null);
    // Mappings target a specific schema's fields, so drop them when the schema
    // changes rather than carrying over stale field references.
    setColumnMappings([]);
  }, []);

  const registryId = selectedPlateSchema?.registryId;

  const allNamesFilled = plates.every((plate) => (namesById[plate.scratchPlateId] ?? '').trim().length > 0);
  const isDetailsValid = allNamesFilled && selectedPlateSchemaId != null;
  const isProjectValid =
    !BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES ||
    (selectedProjectId != null && selectedProjectId !== registryId);
  const isLocationValid = selectedParent.storable != null;
  const canSubmit = isDetailsValid && isProjectValid && isLocationValid && !isSubmitting;

  function setName(scratchPlateId: string, value: string): void {
    setNamesById((prev) => ({...prev, [scratchPlateId]: value}));
  }

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    try {
      const items: Array<BulkPutScratchPlateInInventoryItem> = plates.map((plate) => ({
        scratchPlateId: plate.scratchPlateId,
        name: namesById[plate.scratchPlateId]?.trim() || undefined,
        // A custom barcode can only apply to a single plate; for multiple
        // plates barcodes are always autogenerated (see below).
        barcodeValue:
          isSinglePlate && !shouldAutogenerateBarcode ? customBarcode.trim() || undefined : undefined,
      }));

      // Resolves once the request is accepted, not when the job finishes: a
      // tray task tracks the rest, so we close the modal right away rather than
      // blocking on a potentially long job.
      await submitBulkPutInInventory(
        {
          defaults: {
            plateSchemaId: selectedPlateSchemaId,
            sourceApiId: selectedProjectId ?? undefined,
            parentStorableApiId: selectedParent.storable?.id ?? undefined,
            columnMappings: columnMappings.length > 0 ? columnMappings : undefined,
            studyApiIds: studyIds.length > 0 ? studyIds : undefined,
          },
          items,
        },
        {onCompleted}
      );

      notifications.info(
        isSinglePlate
          ? 'Putting plate in inventory. We will notify you when it is done.'
          : `Putting ${plates.length} plates in inventory. We will notify you when it is done.`
      );
      onRequestSubmit();
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.error(error.message);
      } else if (error != null && typeof error === 'object') {
        notifications.notifyError(error as Response);
      } else {
        throw error;
      }
    } finally {
      // Skip the state update if a successful submit already unmounted the modal.
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', cancel: true, style: 'default'},
    {text: isSubmitting ? 'Creating...' : 'Create', submit: true, style: 'primary', disabled: !canSubmit},
  ];

  return (
    <Modal
      title={isSinglePlate ? 'Put plate in inventory' : `Put ${plates.length} plates in inventory`}
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
    >
      <FormGroup>
        {isSinglePlate ? (
          <TextField
            isRequired={true}
            label="Plate name"
            placeholder="Enter plate name..."
            value={namesById[plates[0].scratchPlateId] ?? ''}
            onChange={(e) => {
              setName(plates[0].scratchPlateId, e.target.value);
            }}
            autoFocus={true}
          />
        ) : (
          <>
            <FieldLabel isRequired={true} label="Plate names" labelStyle={FieldLabel.STYLES.LABEL} />
            <Stack spacing="xs" mt="xs">
              {plates.map((plate, index) => (
                <TextField
                  key={plate.scratchPlateId}
                  labelStyle={FieldLabel.STYLES.HIDDEN}
                  label={`Name for ${plate.scratchPlateName ?? plate.scratchPlateId}`}
                  placeholder="Enter plate name..."
                  value={namesById[plate.scratchPlateId] ?? ''}
                  onChange={(e) => {
                    setName(plate.scratchPlateId, e.target.value);
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Stack>
          </>
        )}
      </FormGroup>

      <FormGroup>
        <FieldLabel isRequired={true} label="Plate schema" labelStyle={FieldLabel.STYLES.LABEL} />
        <Text className={classes.helpText}>
          Only schemas matching the plate dimensions ({plateSize[0]} x {plateSize[1]}) are shown.
        </Text>
        <PlateSchemaSelector
          plateSchemaType={PlateSchemaType.FIXED_PLATE}
          plateSchemaFilter={plateSchemaFilter}
          onChange={handlePlateSchemaChange}
          registryId={undefined}
          selectedPlateSchemaId={selectedPlateSchemaId}
          disabled={false}
          shouldShowNoSchemaOption={false}
        />
      </FormGroup>

      {selectedContainerSchema != null && mappableLayers.length > 0 && (
        <FormGroup>
          <FieldLabel label="Column mapping" labelStyle={FieldLabel.STYLES.LABEL} />
          <Text className={classes.helpText}>
            Map columns from the scratch plate onto fields on the new {isSinglePlate ? 'plate' : 'plates'}
            &apos; wells.
          </Text>
          <ScratchPlateColumnMappingTable
            schemaFields={selectedContainerSchema.fields}
            layers={mappableLayers}
            mappings={columnMappings}
            setMappings={setColumnMappings}
          />
        </FormGroup>
      )}

      <FormGroup>
        <ParentStorableDataSelector
          label="Location"
          isRequired={true}
          selectedParent={selectedParent}
          onUpdate={setSelectedParent}
          registryId={registryId}
          storableSchema={
            selectedPlateSchema != null
              ? {id: selectedPlateSchema.id, registryId: selectedPlateSchema.registryId}
              : null
          }
          allowNone={false}
        />
      </FormGroup>

      {isSinglePlate ? (
        <BarcodeInput
          shouldAutogenerateBarcode={shouldAutogenerateBarcode}
          customBarcode={customBarcode}
          onUpdate={(update) => {
            if (update.shouldAutogenerateBarcode != null) {
              setShouldAutogenerateBarcode(update.shouldAutogenerateBarcode);
            }
            if (update.customBarcode != null) {
              setCustomBarcode(update.customBarcode);
            }
          }}
        />
      ) : (
        <FormGroup>
          <FieldLabel label="Barcode" labelStyle={FieldLabel.STYLES.LABEL} />
          <Text className={classes.helpText}>
            Barcodes are autogenerated when putting multiple plates in inventory.
          </Text>
        </FormGroup>
      )}

      <FormGroup>
        <FieldLabel label="Project" labelStyle={FieldLabel.STYLES.LABEL} />
        <ConnectedStorableSourceSelector
          isWritable={true}
          onChange={setSelectedProjectId}
          registryId={registryId ?? null}
          selectedSourceId={selectedProjectId}
          selectorModalZIndex={1060}
        />
      </FormGroup>

      {BenchlingConfig.STUDIES_ENABLE && (
        <BaseStorablePaneStudyableFormGroup
          studyIds={studyIds}
          onUpdate={(state) => {
            setStudyIds(state.studyIds ?? []);
          }}
        />
      )}
    </Modal>
  );
}

export default PutScratchPlateInInventoryModal;

const useStyles = createStyles((theme) => ({
  helpText: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.spacing.xs,
  },
}));

export function openPutScratchPlateInInventoryModal(props: {
  plates: Array<ScratchPlateInput>;
  /** Called after the request is accepted, e.g. to clear the caller's selection. */
  onSubmitted?: () => void;
  /** Called after the async bulk job reaches a terminal state with successes. */
  onCompleted?: () => void;
}): void {
  const {plates, onSubmitted, onCompleted} = props;
  // The modal seeds its shared fields from plates[0], so an empty list is a
  // caller bug (e.g. opening the modal with nothing selected). Bail out with a
  // clear error rather than mounting a modal that would crash on plates[0].
  if (plates.length === 0) {
    throw new Error('openPutScratchPlateInInventoryModal requires at least one plate.');
  }
  const mount = document.createElement('div');
  document.body.appendChild(mount);

  function cleanup(): void {
    ReactDOM.unmountComponentAtNode(mount);
    mount.remove();
  }

  renderToDOM(
    <PutScratchPlateInInventoryModal
      plates={plates}
      onRequestClose={cleanup}
      onRequestSubmit={() => {
        onSubmitted?.();
        cleanup();
      }}
      onCompleted={onCompleted}
    />,
    mount
  );
}
